import Vue from 'vue';
import VueRouter from 'vue-router';
import LocalStorage from '@/utils/LocalStorage';
import { validateSession } from '@/utils/Auth';
import { redirectToOnboarding, createWallet } from '@/utils/Router';
import store from '@/store';
import getQueryParams from '@/utils/getQueryParams';

import TradeSellOfferView from '@/views/trading/TradeSellOfferView.vue';
import SellOfferView from '@/views/trading/SellOfferView.vue';
import MySellOffersView from '@/views/trading/MySellOffersView.vue';
import MyTradesView from '@/views/trading/MyTradesView.vue';
import MySecuritiesView from '@/views/trading/MySecuritiesView.vue';
import NotFound from '@/views/NotFound.vue';

Vue.use(VueRouter);

const routes = [
  {
    path: '/selloffer/:sellOfferId',
    name: 'tradeSellOffer',
    meta: { requiresAuth: true },
    component: TradeSellOfferView,
  },
  {
    path: '/selloffer',
    name: 'sellOffer',
    meta: { requiresAuth: true },
    component: SellOfferView,
    beforeEnter(to, from, next) {
      if (to.query.asset_id) {
        next();
      } else {
        next({ name: 'not-found' });
      }
    },
  },
  {
    path: '/my/selloffers',
    name: 'mySellOffers',
    meta: { requiresAuth: true },
    component: MySellOffersView,
  },
  {
    path: '/my/trades',
    name: 'myTrades',
    meta: { requiresAuth: true },
    component: MyTradesView,
  },
  {
    path: '/my/securities',
    name: 'mySecurities',
    meta: { requiresAuth: true },
    component: MySecuritiesView,
  },
  {
    path: '/',
    redirect: {
      name: 'mySecurities',
    },
    name: 'main',
    meta: { requiresAuth: true },
  },
  {
    path: '*',
    name: 'not-found',
    component: NotFound,
  },
];

const router = new VueRouter({
  mode: window['isDev'] ? 'hash' : 'history',
  routes,
});

router.beforeEach(async (to, from, next) => {
  const { redirect_url } = getQueryParams(to.query);

  if (redirect_url) {
    store.dispatch('appState/setRedirectUrl', redirect_url);
  }

  // handle route redirects
  if (LocalStorage.has('REDIRECT_TO')) {
    const redirect = LocalStorage.get('REDIRECT_TO');
    LocalStorage.remove('REDIRECT_TO');

    return next(redirect);
  }

  const validSession = await validateSession();

  // routes that require authentication
  if (to.matched.some(route => route.meta.requiresAuth)) {
    if (!validSession) {
      // user not signed in. store route to use after cockpit signin redirection
      const { fullPath } = to;
      LocalStorage.add('REDIRECT_TO', fullPath);

      return redirectToOnboarding();
    }

    // check that user is already loaded, if not, fetch user data
    if (!store.getters['profile/user']) {
      await store.dispatch('profile/fetchIdToken');
      await store.dispatch('profile/fetchUser');
    }

    // check that wallet is already loaded, if not, fetch wallet data
    if (!store.getters['wallet/wallet']) {
      try {
        await store.dispatch('wallet/fetchWallet');
      } catch (error) {
        if (error.status !== 200) {
          // user does not have a wallet. Show create wallet
          const { fullPath } = to;
          LocalStorage.add('REDIRECT_TO', fullPath);

          if(error.status === 429) {
            // rate limit in mangopay has been reached
            // prevent application to redirect user to user service to avoid infinite redirections loop
            return Promise.reject(null);
          }

          return redirectToOnboarding();
        }
      }
    }
  }

  return next();
});

export default router;
